import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary: [
          // Modern primary button - clean gradient with subtle depth
          'relative bg-gradient-to-b from-primary-500 to-primary-600',
          'text-white font-medium',
          'border border-primary-500/50',
          'shadow-md shadow-primary-500/20',
          // Hover - lift and brighten
          'hover:from-primary-400 hover:to-primary-500',
          'hover:shadow-lg hover:shadow-primary-500/30',
          'hover:-translate-y-0.5',
          // Active - pressed
          'active:from-primary-600 active:to-primary-700',
          'active:shadow-sm active:translate-y-0',
        ].join(' '),
        secondary: [
          'bg-white/[0.06] backdrop-blur-lg text-slate-200',
          'border border-white/10',
          'hover:bg-white/[0.1] hover:border-white/15 hover:text-white',
          'active:bg-white/[0.08]',
        ].join(' '),
        ghost: 'text-slate-400 hover:bg-white/[0.06] hover:text-white',
        danger: [
          'bg-red-500/10 text-red-400',
          'border border-red-500/20',
          'hover:bg-red-500/20 hover:border-red-500/30',
          'active:bg-red-500/15',
        ].join(' '),
        success: [
          'bg-emerald-500/10 text-emerald-400',
          'border border-emerald-500/20',
          'hover:bg-emerald-500/20 hover:border-emerald-500/30',
          'active:bg-emerald-500/15',
        ].join(' '),
        accent: [
          'bg-gradient-to-b from-accent-500 to-accent-600 text-white',
          'shadow-md shadow-accent-500/20',
          'hover:from-accent-400 hover:to-accent-500 hover:shadow-lg hover:shadow-accent-500/30',
          'active:from-accent-600 active:to-accent-700',
        ].join(' '),
      },
      size: {
        sm: 'h-8 px-3 text-sm gap-1.5',
        md: 'h-10 px-4 text-sm gap-2',
        lg: 'h-12 px-6 text-base gap-2',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
